import _ from 'lodash';

import {
  C2SCancelMessage,
  C2SCancelMessageName,
  C2SExecuteMessage,
  C2SExecuteMessageName,
  InternalMessage,
  S2CErrorMessage,
  S2CErrorMessageName,
  S2CProgressMessage,
  S2CProgressMessageName,
  S2CSuccessMessage,
  S2CSuccessMessageName,
} from './InternalMessages';
import { makeCanceledError, WorkerOperationState } from './WorkerOperationState';

export interface WorkerClientInput {
  name: string;
  args?: any;
  onProgress?: (progress: number) => void;
}

export interface WorkerClientInputTyped<T> extends WorkerClientInput {
  args?: T;
}

export interface WorkerOperationContext {
  uniqueOperationId: string;
  input: WorkerClientInput;
  progress: number;
  state: WorkerOperationState;
  promise: Promise<any>;
}

interface PromiseCallbacks {
  fulfill: (result: any) => void;
  reject: (error: Error) => void;
}

interface WorkerOperationContextPrivate extends WorkerOperationContext {
  promiseCallbacks: PromiseCallbacks;
}

export interface WorkerClient {
  onCustomMessage?: (msg: InternalMessage) => void;

  execute(input: WorkerClientInput): WorkerOperationContext;
  cancel(operationId: string): void;
  shutdown(): void;
}

export abstract class WorkerClientFactory {
  static make(worker: Worker): WorkerClient {
    return new WorkerClientImpl(worker);
  }
}

class WorkerClientImpl implements WorkerClient {
  onCustomMessage?: (msg: InternalMessage) => void;

  private worker: Worker;
  private activeOperations = new Map<string, WorkerOperationContextPrivate>();

  constructor(worker: Worker) {
    this.worker = worker;
    this.subscribeForWorkerEvents();
  }

  execute(input: WorkerClientInput): WorkerOperationContext {
    let callbacks: PromiseCallbacks;
    const promise = new Promise((fulfill, reject) => {
      callbacks = {
        fulfill,
        reject,
      };
    });

    const ctx: WorkerOperationContextPrivate = {
      uniqueOperationId: _.uniqueId('operation_'),
      input,
      progress: 0,
      state: WorkerOperationState.InProgress,
      promise,
      promiseCallbacks: callbacks!,
    };
    this.activeOperations.set(ctx.uniqueOperationId, ctx);

    const msg: C2SExecuteMessage = {
      internalName: C2SExecuteMessageName,
      originalName: input.name,
      originalArgs: input.args,
      operationId: ctx.uniqueOperationId,
    };

    const timeLabel = `WorkerClient.postMessage ${input.name}`;
    console.time(timeLabel);
    this.worker.postMessage(msg);
    console.timeEnd(timeLabel);

    return ctx;
  }

  cancel(operationId: string): void {
    const ctx = this.activeOperations.get(operationId);
    if (!ctx) {
      console.warn(`No such operation ${operationId} to cancel`);
      return;
    }

    if (ctx.state !== WorkerOperationState.InProgress) {
      return;
    }

    ctx.state = WorkerOperationState.Canceled;
    ctx.promiseCallbacks.reject(makeCanceledError());

    const msg: C2SCancelMessage = {
      internalName: C2SCancelMessageName,
      operationId: ctx.uniqueOperationId,
    };
    this.worker.postMessage(msg);

    this.activeOperations.delete(operationId);
  }

  shutdown(): void {
    this.worker.terminate();
  }

  private subscribeForWorkerEvents() {
    if (this.worker.onmessage !== null) {
      throw `onmessage should be null, worker should not be reused in multiple WorkerClient's`;
    }

    this.worker.onmessage = (args: { data: InternalMessage }) => {
      const message = args.data;

      let ctx: WorkerOperationContextPrivate | undefined;

      switch (message.internalName) {
        case S2CProgressMessageName:
          const progressMsg = message as S2CProgressMessage;
          ctx = this.activeOperations.get(progressMsg.operationId);
          if (!ctx) {
            console.warn(
              `Got message ${message.internalName} for an absent operation with ID ${progressMsg.operationId}`,
            );
            return;
          }

          ctx.progress = progressMsg.progress;
          if (ctx.input.onProgress) {
            ctx.input.onProgress(ctx.progress);
          }
          break;
        case S2CSuccessMessageName:
          const successMsg = message as S2CSuccessMessage;
          ctx = this.activeOperations.get(successMsg.operationId);
          if (!ctx) {
            console.warn(
              `Got message ${message.internalName} for an absent operation with ID ${successMsg.operationId}`,
            );
            return;
          }

          ctx.state = WorkerOperationState.Succeeded;
          ctx.promiseCallbacks.fulfill(successMsg.result);
          this.activeOperations.delete(successMsg.operationId);
          break;
        case S2CErrorMessageName:
          const errorMessage = message as S2CErrorMessage;
          ctx = this.activeOperations.get(errorMessage.operationId);
          if (!ctx) {
            console.warn(
              `Got message ${message.internalName} for an absent operation with ID ${errorMessage.operationId}`,
            );
            return;
          }

          ctx.state = WorkerOperationState.Failed;
          ctx.promiseCallbacks.reject(errorMessage.error);
          this.activeOperations.delete(errorMessage.operationId);
          break;
        default:
          if (this.onCustomMessage) {
            this.onCustomMessage(message);
          } else {
            console.error(
              `Cannot deliver custom message ${message.internalName} because callback is not set`,
            );
          }
          break;
      }
    };
  }
}
